import React, { useContext, useEffect, useRef, useState } from 'react';
import Context from '../../Context';
import FieldWrapper from '../fields/FieldWrapper';
import { FIELD_META_MAP } from '../../utils/constants';
import dayjs from 'dayjs';
import DateField from './DateField';
import CheckboxField from './CheckboxField';
import cn from 'classnames';
import NumberField from './NumberField';
import Icon from 'chameleon/ui-stack/ui/base/icon';
import DATA_TEST_IDS from '../../utils/dataTestIds';

import './styles.scss';

const TimeOptions = () => {
  const {
    isAssign,
    startsAt,
    dueAt,
    printToggle,
    timer,
    allowLateSubmission,
    allAssignees,
    manageSettingsDetailsRef,
    sectionsMinTimezone,
    startDate,
    hasStartsAtError,
    showTimer,
    showStartDateResetButton,
    isResourceForm,
    allowLateSubmissionFF,
    allowHardTimer,
    isSoftTimer,
  } = useContext(Context);
  const [startDateDivAlignmentHeight, setStartDateDivAlignmentHeight] =
    useState(0);
  const currentHasStartsAtError = useRef(false);

  useEffect(() => {
    const setAlignmentDivs = () => {
      const manageSettingsDetailsHeight = manageSettingsDetailsRef.current
        ? manageSettingsDetailsRef.current.getBoundingClientRect().height
        : 0;
      // If we have the start date error, we have to lower the height of the div by 20 (the height of the error).
      const newStartDateDivAlignmentHeight = currentHasStartsAtError.current
        ? manageSettingsDetailsHeight - 20
        : manageSettingsDetailsHeight;
      setStartDateDivAlignmentHeight(
        newStartDateDivAlignmentHeight >= 0
          ? newStartDateDivAlignmentHeight
          : 0,
      );
    };

    setAlignmentDivs();
    window.addEventListener('resize', setAlignmentDivs);

    return () => {
      window.removeEventListener('resize', setAlignmentDivs);
    };
  }, []);

  useEffect(() => {
    const startsAtErrorRaised =
      !currentHasStartsAtError.current && hasStartsAtError;
    const startsAtErrorVanished =
      currentHasStartsAtError.current && !hasStartsAtError;

    if (startsAtErrorRaised) {
      setStartDateDivAlignmentHeight(startDateDivAlignmentHeight - 20);
    } else if (startsAtErrorVanished) {
      setStartDateDivAlignmentHeight(startDateDivAlignmentHeight + 20);
    }

    currentHasStartsAtError.current = hasStartsAtError;
  }, [hasStartsAtError]);

  const momentNow = dayjs().tz(sectionsMinTimezone);

  const startsAtMinDate = dayjs(startDate).isAfter(momentNow)
    ? dayjs(startDate)
    : momentNow;
  const startsAtMaxDate = dueAt;

  const shouldIncludeAllowLateSubmission = !isAssign && allowLateSubmissionFF;
  const shouldIncludeUpdatedTimerControls = !isAssign && allowHardTimer;

  return (
    <div className="ft-amf-assignment-time-container">
      <div
        className="ft-amf-option-container"
        data-test-id={FIELD_META_MAP.START_DATE.dataTestId}
      >
        <FieldWrapper field={FIELD_META_MAP.START_DATE}>
          <DateField
            hasResetButton={showStartDateResetButton}
            name="startsAt"
            selectedValue={
              startsAt ? startsAt.format('YYYY-MM-DD HH:mm:ss') : null
            }
            isFinalExam={false} // pre-ap prop
            hasMinDate
            minDate={startsAtMinDate}
            maxDate={startsAtMaxDate}
            disabled={printToggle}
            placeholderText={
              <div className="flex">
                <Icon id="horizontal_mini_line" />
              </div>
            }
            overridingProps={{
              role: 'textbox',
              'data-ft-id': 'startsAt',
              class: 'ft-amf-date-picker-control',
            }}
            defaultTimezone={sectionsMinTimezone}
            sections={allAssignees}
            hasReset
            isNewModal
            isAssign={isAssign}
            hideTodayButton={false} // pre-ap prop
          />
        </FieldWrapper>
        {
          // Remove `ft-amf-option-details` class if we have the start date error. The error already have the margin-top.
          manageSettingsDetailsRef.current && (
            <div
              className={cn('visuallyhidden', {
                'ft-amf-option-details': !hasStartsAtError,
              })}
              style={{
                height: startDateDivAlignmentHeight,
                width: '100%',
              }}
            />
          )
        }
      </div>
      <div
        className="ft-amf-option-container"
        data-test-id={FIELD_META_MAP.DUE_DATE.dataTestId}
      >
        <FieldWrapper field={FIELD_META_MAP.DUE_DATE}>
          <DateField
            name="dueAt"
            hasMinDate
            minDate={startsAt}
            openToDate={startsAt}
            popperPlacement="top-end"
            selectedValue={dueAt ? dueAt.format('YYYY-MM-DDTHH:mm:ss') : null}
            disabled={printToggle}
            placeholderText={
              <div className="flex">
                <Icon id="horizontal_mini_line" />
              </div>
            }
            overridingProps={{
              role: 'textbox',
              'data-ft-id': 'dueAt',
              class: 'ft-amf-date-picker-control',
            }}
            defaultTimezone={sectionsMinTimezone}
            sections={allAssignees}
            hasReset
            isNewModal
            isAssign={isAssign}
          />
        </FieldWrapper>
        {!!dueAt && !isResourceForm && (
          <>
            <div
              className="ft-amf-option-details text-gray-600 mb-4"
              role="region"
              aria-live="polite"
            >
              {FIELD_META_MAP.DUE_DATE.detailsComponent({
                allowLateSubmission: shouldIncludeAllowLateSubmission,
              })}
            </div>
            {shouldIncludeAllowLateSubmission && (
              <CheckboxField
                name="allowLateSubmission"
                value={allowLateSubmission}
                title="Allow Late Submissions"
              />
            )}
          </>
        )}
      </div>
      {showTimer && (
        <div
          className="ft-amf-option-container"
          data-test-id={FIELD_META_MAP.TIMER.dateTestId}
        >
          <FieldWrapper field={FIELD_META_MAP.TIMER}>
            <NumberField isDisabled={printToggle} name="timer" timer={timer} />
          </FieldWrapper>
          {!!timer && (
            <>
              <div
                className="ft-amf-option-details text-gray-600 mb-4"
                role="region"
                aria-live="polite"
              >
                {FIELD_META_MAP.TIMER.detailsComponent({
                  allowHardTimer: shouldIncludeUpdatedTimerControls,
                })}
              </div>
              {shouldIncludeUpdatedTimerControls && (
                <CheckboxField
                  name="isSoftTimer"
                  value={isSoftTimer}
                  data-test-id={DATA_TEST_IDS.UPDATE_SETTINGS_IS_SOFT_TIMER}
                  title="Allow submissions after timer expires"
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

TimeOptions.displayName = 'TimeOptions';

export default TimeOptions;
